import { openapiClient, unwrapResult } from '@/api/openapi-client';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type {
  ExceptionCreateBody,
  ExceptionUpdateBody,
  OpeningHoursException,
} from '@restaurant/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const EXCEPTIONS_PATH = '/api/restaurants/{restaurantId}/exceptions';
const EXCEPTION_PATH = '/api/restaurants/{restaurantId}/exceptions/{exceptionId}';

export function useExceptions() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId);
  return useQuery({
    queryKey: ['exceptions', restaurantId],
    queryFn: async () => {
      if (!restaurantId) throw new Error('No restaurant selected');
      return unwrapResult<OpeningHoursException[]>(
        await openapiClient.GET(EXCEPTIONS_PATH, { params: { path: { restaurantId } } }),
      );
    },
    enabled: !!restaurantId,
  });
}

export function useCreateException() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ExceptionCreateBody) => {
      if (!restaurantId) throw new Error('No restaurant selected');
      return unwrapResult<OpeningHoursException>(
        await openapiClient.POST(EXCEPTIONS_PATH, { params: { path: { restaurantId } }, body }),
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['exceptions', restaurantId] }),
  });
}

export function useUpdateException() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      exceptionId,
      data,
    }: {
      exceptionId: string;
      data: ExceptionUpdateBody;
    }) => {
      if (!restaurantId) throw new Error('No restaurant selected');
      return unwrapResult<OpeningHoursException>(
        await openapiClient.PATCH(EXCEPTION_PATH, {
          params: { path: { restaurantId, exceptionId } },
          body: data,
        }),
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['exceptions', restaurantId] }),
  });
}

export function useDeleteException() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (exceptionId: string) => {
      if (!restaurantId) throw new Error('No restaurant selected');
      return unwrapResult(
        await openapiClient.DELETE(EXCEPTION_PATH, {
          params: { path: { restaurantId, exceptionId } },
        }),
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['exceptions', restaurantId] }),
  });
}
